import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Grid } from "@material-ui/core";
import CustomerDetails from "./CustomerDetails/CustomerDetails";
import { useParams } from "react-router";

import { OrderDetails as OrderDetailsTypes, WarningsType } from "../Types";

import { useAppDispatch, useAppSelector } from "redux/hooks";
import {
  getCustomerDetailsAsync,
  getIdVerificationAsync,
  getOrderDetailsAsync,
  getOrderWarningsAsync,
} from "../reduxSlice/actionsAsync";
import {
  selectCustomerDetails,
  selectCustomerDetailsLoaded,
  selectNoteAdded,
  selectOrderDetails,
  selectOrderedForOthersWarning,
  selectOrderWarnings,
  selectOrderWarningsLoader,
} from "../reduxSlice/selector";
import { CLINICAL_REVIEW_STATUSES } from "../constants";
import { useHistory } from "react-router-dom";
import "./index.scss";

export interface PropsTypes {
  details: OrderDetailsTypes;
  warnings: WarningsType[];
  warningsLoading?: boolean;
  orderedForOthersWarning?: WarningsType;
  onUpdateSuccess: (showUpdateOrder?: boolean) => void;
  isUpdateOrderModalOpen: boolean;
  onUpdateImages: (upload: boolean) => void;
  refreshOrderDetails: () => void;
}

const ORDER_LIMIT = 10;
const WEIGHT_LOSS_SECONDARY_TAG = "onlinedoctorweightloss";
const SKIP_PHOTO_WARNING: WarningsType = {
  id: "SkipPhoto",
  header: "Image upload skipped, medicated weight loss",
  descriptions: [
    "Please verify the patient's BMI before approving item. Request and upload images if required.",
  ],
};

const getSubscriptionWarningContext = (orderDetails: OrderDetailsTypes | null) => {
  if (!orderDetails?.products?.length) {
    return { warningOrderId: undefined, subscriptionProductTitle: "" };
  }

  const currentOrderId = Number(orderDetails.orderId);
  const subscriptionProduct = orderDetails.products.find(
    (item) =>
      item.subscriptionId &&
      item.subscriptionBaseOrderId &&
      currentOrderId !== item.subscriptionBaseOrderId,
  );

  return {
    warningOrderId: subscriptionProduct?.subscriptionBaseOrderId,
    subscriptionProductTitle: subscriptionProduct?.productTitle ?? "",
  };
};

const getWarningsForOrder = (
  warnings: WarningsType[],
  subscriptionProductTitle: string,
) => {
  if (!subscriptionProductTitle) {
    return warnings;
  }

  return warnings.filter(
    (warning) =>
      warning.header?.toLowerCase() === subscriptionProductTitle.toLowerCase(),
  );
};

const shouldAddSkipPhotoWarning = (orderDetails: OrderDetailsTypes) => {
  const hasPhotoSkippedProduct = orderDetails.products.some(
    (product) =>
      product.isPhotoUploadSkipped &&
      product.secondaryTag.toLowerCase() === WEIGHT_LOSS_SECONDARY_TAG,
  );

  if (!hasPhotoSkippedProduct) {
    return false;
  }

  const hasProductNote = (orderDetails.orderNotes ?? []).some(
    (note) => note.productId !== 0,
  );

  return !hasProductNote;
};

const withOrderDetailsData = (WrappedComponent: React.FC<PropsTypes>) => () => {
  const dispatch = useAppDispatch();
  const orderDetails = useAppSelector(selectOrderDetails);
  const customerDetails = useAppSelector(selectCustomerDetails);
  const noteAdded = useAppSelector(selectNoteAdded);
  const warnings = useAppSelector(selectOrderWarnings);
  const warningsLoader = useAppSelector(selectOrderWarningsLoader);
  const customerDetailsLoader = useAppSelector(selectCustomerDetailsLoaded);
  const orderedForOthersWarning = useAppSelector(selectOrderedForOthersWarning);
  const [isUpdateOrderModalOpen, setIsUpdateOrderModalOpen] = useState<boolean>(false);
  const [isBMIOrUploadImages, setIsBMIOrUploadPhotoImages] = useState(false);
  const history = useHistory();
  const { id: orderId } = useParams<{ id?: string }>();
  const orderIdNumber = useMemo(() => Number(orderId), [orderId]);
  const isValidOrderId = Number.isFinite(orderIdNumber);
  const customerAddressDetails = customerDetails?.addressDetails;
  const orderCustomerId = orderDetails?.customerId;

  const { warningOrderId, subscriptionProductTitle } = useMemo(
    () => getSubscriptionWarningContext(orderDetails),
    [orderDetails],
  );

  const getWarnings = useCallback(
    (orderWarningId: number | undefined) => {
      if (!orderDetails) {
        return;
      }

      dispatch(
        getOrderWarningsAsync({
          orderId: orderWarningId ?? Number(orderDetails.orderId),
          customerId: orderDetails.customerId,
        }),
      );
    },
    [dispatch, orderDetails],
  );

  useEffect(() => {
    if (!isValidOrderId) {
      return;
    }

    dispatch(getOrderDetailsAsync(orderIdNumber));
  }, [
    dispatch,
    orderIdNumber,
    noteAdded,
    isBMIOrUploadImages,
    customerAddressDetails,
    isValidOrderId,
  ]);

  useEffect(() => {
    if (!orderDetails) {
      return;
    }

    getWarnings(warningOrderId);
  }, [getWarnings, orderDetails, warningOrderId]);

  useEffect(() => {
    if (!orderCustomerId) {
      return;
    }

    dispatch(getCustomerDetailsAsync({ customerId: orderCustomerId }));
  }, [dispatch, orderCustomerId]);

  const refreshOrderDetails = useCallback(() => {
    if (!isValidOrderId) {
      return;
    }

    dispatch(getOrderDetailsAsync(orderIdNumber));
  }, [dispatch, isValidOrderId, orderIdNumber]);

  const onUpdateSuccess = useCallback((showUpdateOrder?: boolean) => {
    if (!orderDetails) {
      return;
    }

    dispatch(getOrderDetailsAsync(Number(orderDetails.orderId)));
    dispatch(getIdVerificationAsync({
      page: 1,
      limit: ORDER_LIMIT,
      status: CLINICAL_REVIEW_STATUSES.AWAITING_ID_CHECK,
    }));
    if (showUpdateOrder) {
      setIsUpdateOrderModalOpen(true);
    }
  }, [dispatch, orderDetails]);

  const orderWarnings = useMemo(() => {
    if (!orderDetails) {
      return [];
    }

    const orderScopedWarnings = getWarningsForOrder(
      warnings,
      subscriptionProductTitle,
    );
    if (shouldAddSkipPhotoWarning(orderDetails)) {
      return [...orderScopedWarnings, SKIP_PHOTO_WARNING];
    }

    return orderScopedWarnings;
  }, [orderDetails, subscriptionProductTitle, warnings]);

  return (
    <>
      <button className="back-button mt-16 mb-16" onClick={history.goBack}>
        Back
      </button>
      <Grid container className="crp_container">
        <Grid xs={12} sm={4} lg={3} className="crp_customer_details">
          <CustomerDetails
            customerId={orderDetails?.customerId ?? 0}
            customerDetails={customerDetails}
            loading={customerDetailsLoader}
            onUpdateSuccess={refreshOrderDetails}
            orderDetails={orderDetails}
          />
        </Grid>
        <Grid xs={12} sm={8} lg={9}>
          {orderDetails && (
            <WrappedComponent
              details={orderDetails}
              warnings={orderWarnings}
              warningsLoading={warningsLoader}
              orderedForOthersWarning={orderedForOthersWarning}
              onUpdateSuccess={onUpdateSuccess}
              isUpdateOrderModalOpen={isUpdateOrderModalOpen}
              onUpdateImages={setIsBMIOrUploadPhotoImages}
              refreshOrderDetails={refreshOrderDetails}
            />
          )}
        </Grid>
      </Grid>
    </>
  );
};

export default withOrderDetailsData;
